import { type DateRange, Period } from '../model';

export function addPeriod(date: Date, period: Period, count: number) {
  const res = new Date(date);
  switch (period) {
    case Period.YEAR:
      res.setUTCFullYear(res.getUTCFullYear() + count);
      return res;
    case Period.MONTH:
      res.setUTCMonth(res.getUTCMonth() + count);
      return res;
    case Period.WEEK:
      res.setUTCDate(res.getUTCDate() + count * 7);
      return res;
    case Period.DAY:
      res.setUTCDate(res.getUTCDate() + count);
      return res;
    case Period.HOUR:
      res.setUTCHours(res.getUTCHours() + count);
      return res;
    default:
      throw new Error(`Unsupported period ${period}`);
  }
}

const firstDayOfWeekIndex = 1; // Monday

export function getPeriodStartDate(date: Date, period: Period, count: number) {
  const res = new Date(date);
  switch (period) {
    case Period.YEAR:
      res.setUTCDate(1);
      res.setUTCMonth(0);
      res.setUTCFullYear(res.getUTCFullYear() + count);
      return res;
    case Period.QUARTER: {
      res.setUTCDate(1);
      const quarterStart = Math.floor(res.getUTCMonth() / 3) * 3;
      res.setUTCMonth(quarterStart + count * 3);
      return res;
    }
    case Period.MONTH:
      res.setUTCDate(1);
      res.setUTCMonth(res.getUTCMonth() + count);
      return res;
    case Period.WEEK: {
      res.setUTCHours(0, 0, 0, 0);
      const day = res.getUTCDay();
      const diffToFirstDay = day >= firstDayOfWeekIndex ? day - firstDayOfWeekIndex : 6 - day;
      const date = res.getUTCDate() - diffToFirstDay;
      res.setUTCDate(date + count * 7);
      return res;
    }
    case Period.DAY:
      res.setUTCHours(0, 0, 0, 0);
      res.setUTCDate(res.getUTCDate() + count);
      return res;
    case Period.HOUR:
      res.setUTCMinutes(0, 0, 0);
      res.setUTCHours(res.getUTCHours() + count);
      return res;
    default:
      throw new Error(`Unsupported period ${period}`);
  }
}

export function getPeriodEndDate(date: Date, period: Period, count: number) {
  switch (period) {
    case Period.YEAR: {
      const end = getPeriodStartDate(date, period, count);
      end.setUTCDate(31);
      end.setUTCMonth(11);
      return end;
    }
    case Period.QUARTER: {
      const end = getPeriodStartDate(date, period, count);
      end.setUTCMonth(end.getUTCMonth() + 3);
      end.setUTCDate(0);
      return end;
    }
    case Period.MONTH: {
      const end = getPeriodStartDate(date, period, count);
      end.setUTCMonth(end.getUTCMonth() + 1);
      end.setUTCDate(0);
      return end;
    }
    case Period.WEEK: {
      const end = getPeriodStartDate(date, period, count);
      end.setUTCDate(end.getUTCDate() + 6);
      return end;
    }
    case Period.DAY: {
      const end = getPeriodStartDate(date, period, count);
      end.setUTCDate(end.getUTCDate() + 1);
      return end;
    }
    case Period.HOUR: {
      const end = getPeriodStartDate(date, period, count);
      end.setUTCHours(end.getUTCHours() + 1);
      return end;
    }
    default:
      throw new Error(`Unsupported period ${period}`);
  }
}

export function* dateRange(range: DateRange, period: Period) {
  const { startDate, endDate } = range;
  let i = 0;
  let date = startDate;
  while (date < endDate) {
    yield date;

    i++;
    date = new Date(getPeriodStartDate(startDate, period, i));
  }
}

export function getDateIndex(startDate: Date, date: Date, period: Period) {
  let i = 0;
  const endDate = date;
  endDate.setUTCMilliseconds(date.getUTCMilliseconds() + 1);
  for (const d of dateRange({ startDate, endDate }, period)) {
    switch (period) {
      case Period.MONTH:
        if (d.getUTCFullYear() === date.getUTCFullYear() && d.getUTCMonth() === date.getUTCMonth())
          return i;
        break;
      case Period.WEEK:
        if (isSameWeek(d, date)) return i;
        break;
      case Period.DAY:
        if (
          d.getUTCFullYear() === date.getUTCFullYear() &&
          d.getUTCMonth() === date.getUTCMonth() &&
          d.getUTCDate() === date.getUTCDate()
        )
          return i;
        break;
      case Period.HOUR:
        if (
          d.getUTCFullYear() === date.getUTCFullYear() &&
          d.getUTCMonth() === date.getUTCMonth() &&
          d.getUTCDate() === date.getUTCDate() &&
          d.getUTCHours() === date.getUTCHours()
        )
          return i;
        break;
      default:
        throw new Error(`Unsupported period ${period}`);
    }
    i++;
  }

  return -1;
}

export function getRangeLength(range: DateRange, period: Period) {
  let i = 0;
  for (const _ of dateRange(range, period)) {
    i++;
  }

  return i;
}

export function getWeekStart(d: Date) {
  const start = new Date(d);

  const day = start.getUTCDay();
  const startDay = day === 0 ? -6 : 1;
  const shiftToStart = (startDay - day) % 7;

  start.setUTCDate(start.getUTCDate() + shiftToStart);
  start.setUTCHours(0, 0, 0, 0);

  return start;
}

export function getWeekEnd(d: Date) {
  const start = getWeekStart(d);
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 6);
  return end;
}

function isSameWeek(date1: Date, date2: Date) {
  const start = getWeekStart(date1);

  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 7);

  return date2 >= start && date2 < end;
}
